import React from "react";
import moment from "moment-timezone";
import styled from "styled-components";
import { useMappedState } from "redux-react-hook";

import { useDrop } from "src/core/drops/hooks";
import { getSingleOrder } from "src/core/drops/utils/inherited-settings";
import { OrderDayPicker } from "src/library/components/DayPicker";
import {
  hasOrders,
  isDayValid
} from "src/library/utils";
import { useDisabledDeliveryDate } from "src/core/shared/hooks/useDisabledDeliveryDate";
import { useWarningDeliveryDates } from "src/core/shared/hooks/useWarningDeliveryDate";
import { Modal } from "src/library/components/Modal";
import { Button } from "src/library/components/Button";
import useTerminology from "src/core/shared/hooks/useTerminology";
import { IStore } from "src/core/shared/store";

import { useOrderDeliveryDates } from "../hooks/useOrderDeliveryDates";
import {
  useOrderCutoffTime,
  useOrderingAvailableForDay
} from "../hooks/validation";
import { IOrder } from "../api/orders.api";
import { ConfirmMultipleOrderModal } from "./Modals";
import { getMaxLeadTime } from "../utils/order-deliveries";
import { useOrderVariant } from "../hooks/useOrderVariant";

interface OrderDateChangePickerModalProps {
  order: IOrder;
  show: boolean;
  dismiss: () => void;
  onSubmit: (orderDeliveryDate: Date) => void;
  isLoading: boolean;
}

/**
 * Allows you to change the delivery date for an order
 */
export const OrderDateChangePickerModal: React.FC<OrderDateChangePickerModalProps> = ({
  show, order, dismiss, onSubmit, isLoading
}) => {
  const [ { orderDates, currentOrders }, { getCalendarOrderDates } ] = useOrderDeliveryDates();
  const [ { drop } ] = useDrop();
  const terminology = useTerminology();
  const singleDailyDelivery = drop && getSingleOrder(drop.groupedSettings);
  const [ orderDate, setOrderDate ] = React.useState<Date | null>(order.orderDeliveryDate || null);
  const [ warnMultipleOrdersOnDay, setWarnMultipleOrdersOnDay ] = React.useState<boolean>(false);
  const isDisabled = useDisabledDeliveryDate(drop);
  const isWarning = useWarningDeliveryDates();
  const orderingNotAvailableForDay = useOrderingAvailableForDay(drop);
  const leadtimeGroups = useMappedState((store: IStore) => store.global.settings?.leadTimeGroups);
  const getOrderCutoff = useOrderCutoffTime();
  const getOrderVariant = useOrderVariant();

  /** based on max lead time and the order - show the earliest available date */
  const minimumDate = React.useMemo(() => {
    if (order && leadtimeGroups) {
      const maxLeadTime = getMaxLeadTime(order, leadtimeGroups || []);

      return moment.utc().add(maxLeadTime?.defaultLeadTimeDays || 0, "days").startOf("day").toDate();
    }

    return null;
  }, [ leadtimeGroups, order ]);

  /**
   * Is the day provided a disabled for selection
   * - by cutoff
   */
  const isDayDisabled = React.useCallback((day: Date) => {
    const disabledByDate = isDisabled(day, currentOrders, getOrderVariant(order.orderVariantId));

    if (disabledByDate.isDisabled) {
      return disabledByDate;
    }

    const cutoff = getOrderCutoff({
      ...order,
      orderDeliveryDate: day
    }, "shortest");

    if (cutoff && moment.utc().isAfter(cutoff)) {
      return {
        isDisabled: true,
        component: (
          <p>
            There is not enough advanced notice to have the currently selected products delivered on this date.
          </p>
        )
      };
    }

    return { isDisabled: false };
  }, [
    currentOrders,
    getOrderCutoff,
    getOrderVariant,
    isDisabled,
    order
  ]);

  const [ currentMonth, setCurrentMonth ] = React.useState<{
    month: number;
    year: number;
  }>({
    month: minimumDate?.getMonth() || moment.utc().toDate().getMonth(),
    year: minimumDate?.getFullYear() || moment.utc().toDate().getFullYear()
  });

  /** Fetch the orders to load into the calendar */
  React.useEffect(() => {
    if (drop?.id) {
      getCalendarOrderDates(drop.id, currentMonth);
    }
  }, [
    currentMonth,
    drop?.id,
    getCalendarOrderDates
  ]);

  /** handle selection of a date in the calendar */
  const onDateSelection = React.useCallback((day: Date) => {
    const dateUnavailable = isDisabled(day, currentOrders, getOrderVariant(order.orderVariantId));

    if (dateUnavailable.isDisabled) {
      setOrderDate(null);
    } else if (orderingNotAvailableForDay(day, getOrderVariant(order.orderVariantId)) === null) {
      setOrderDate(day);
      setWarnMultipleOrdersOnDay(hasOrders(day, orderDates));
    } else {
      setOrderDate(null);
    }
  }, [
    currentOrders,
    getOrderVariant,
    isDisabled,
    order.orderVariantId,
    orderDates,
    orderingNotAvailableForDay
  ]);

  /** place the order only if there is a date */
  const handlePlaceOrder = React.useCallback(() => {
    if (orderDate) {
      onSubmit(orderDate);
    }
  }, [ onSubmit, orderDate ]);

  return (
    <>
      {orderDate && (
        <ConfirmMultipleOrderModal
          onDismiss={() => setWarnMultipleOrdersOnDay(false)}
          open={warnMultipleOrdersOnDay}
          onConfirm={handlePlaceOrder}
          orderDates={orderDates}
          selectedDay={orderDate}
          terminology={terminology}
        />
      )}

      <Modal
        title="Select Delivery Date"
        show={show}
        backgroundAction={dismiss}
        content={(
          <StyledOrderDayPickerModalInner>
            {minimumDate && (
              <>
                <OrderDayPicker
                  singleDailyDelivery={singleDailyDelivery ? singleDailyDelivery.value : false}
                  orderDates={orderDates}
                  date={minimumDate}
                  handleDateChange={onDateSelection}
                  handleMonthChange={(month: Date) => {
                    setCurrentMonth({
                      month: month.getMonth(),
                      year: month.getFullYear()
                    });
                  }}
                  disabled={date => isDayDisabled(date)}
                  fromDate={minimumDate}
                  warning={date => isWarning(date, order.orderVariantId)}
                  toDate={moment
                    .utc()
                    .add(2, "years")
                    .toDate()}
                />

                <Button
                  disabled={!orderDate}
                  onClick={handlePlaceOrder}
                  loading={isLoading}
                >
                  Place order
                </Button>
              </>
            )}
          </StyledOrderDayPickerModalInner>
        )}
      />
    </>
  );
};

const StyledOrderDayPickerModalInner = styled.div`
  width: 350px;
`;
